import {ErrorMessage} from "@hookform/error-message";
import {Box, FormControlLabel, FormGroup, Typography} from "@mui/material";
import Grid from "@mui/material/Grid";
import {useEffect, useRef, useState} from "react";
import {useFormContext, useFormState} from "react-hook-form";
import SignatureCanvas from "react-signature-canvas";

type Props = {
    name: string;
    label: string;
    disabled?: boolean;
    className?: string;
    width?: number;
};

const DigitalSignatureField = ({name, label, className, width}: Props) => {
    const formState = useFormState();
    const sigRef = useRef<any>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const formContext = useFormContext();
    const [displayWidth, setDisplayWidth] = useState<number>(width ?? 300);

    const onEndSigning = (): void => {
        if (sigRef.current !== undefined) {
            if (sigRef.current.isEmpty()) {
                formContext.setValue(name, null);
                return;
            }
            const signature = sigRef.current.toDataURL("image/png");
            formContext.setValue(name, signature);
        }
    };

    const clearSignature = (): void => {
        if (sigRef.current !== undefined) {
            sigRef.current.clear();
            formContext.setValue(name, null);
        }
    };

    useEffect(() => {
        if (
            sigRef.current !== undefined &&
            sigRef.current.isEmpty() &&
            formContext.getValues(name) !== undefined
        ) {
            sigRef.current.fromDataURL(formContext.getValues(name));
            return;
        }
    }, [formContext.getValues(name)]);

    useEffect(() => {
        const updateWidth = () => {
            const maxWidth = containerRef.current?.offsetWidth ?? undefined;

            if (!width) {
                setDisplayWidth(300);
                return;
            }

            if (maxWidth && maxWidth < width) {
                setDisplayWidth(maxWidth);
                return;
            }
            setDisplayWidth(width);
        };
        window.addEventListener("resize", updateWidth);
        return () => window.removeEventListener("resize", updateWidth);
    }, [containerRef.current, width]);

    return (
        <FormGroup ref={containerRef}>
            <Grid container>
                {label && (
                    <Grid
                        size={{
                            xs: 12,
                            sm: 9,
                        }}
                    >
                        <FormControlLabel
                            sx={{marginLeft: 0}}
                            label={label}
                            control={
                                <input
                                    type="hidden"
                                    name={name}
                                    value={
                                        formContext.getValues(name) === null
                                            ? ""
                                            : formContext.getValues(name)
                                    }
                                    className={className ? className : ""}
                                />
                            }
                        />
                    </Grid>
                )}
                <Grid
                    size={{
                        xs: 12,
                        sm: 3,
                    }}
                >
                    <Box
                        sx={{
                            cursor: "pointer",
                            textAlign: "right",
                        }}
                        onClick={clearSignature}
                    >
                        Clear
                    </Box>
                </Grid>
            </Grid>
            <Grid>
                <SignatureCanvas
                    penColor="black"
                    canvasProps={{
                        width: displayWidth,
                        height: 200,
                        className: "sigCanvas",
                    }}
                    ref={sigRef}
                    onEnd={onEndSigning}
                />
            </Grid>
            <Grid container>
                <Box
                    sx={{
                        px: 2,
                    }}
                >
                    <ErrorMessage
                        errors={formState.errors}
                        name={name}
                        render={() => (
                            <Typography sx={{color: "red"}}>Signature is required</Typography>
                        )}
                    />
                </Box>
            </Grid>
        </FormGroup>
    );
};

export default DigitalSignatureField;
